import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BRAND_PRIMARY } from "../utils/constants";

interface Account {
    _id: string;
    name: string;
    addresses: { index: number; address: string }[];
}

interface CreateBotModalProps {
    show: boolean;
    onClose: () => void;
    newBot: { instanceId: string; poolId: string; accountId: string };
    setNewBot: React.Dispatch<React.SetStateAction<{ instanceId: string; poolId: string; accountId: string }>>;
    accounts: Account[];
    creatingBot: boolean;
    createBotError: string | null;
    onCreateBot: () => void;
}

const CreateBotModal: React.FC<CreateBotModalProps> = ({
    show,
    onClose,
    newBot,
    setNewBot,
    accounts,
    creatingBot,
    createBotError,
    onCreateBot,
}) => {
    const navigate = useNavigate();
    const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
    const accountDropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target as Node)) {
                setAccountDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (!show) return null;

    return (
        <div style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 200,
            padding: "16px",
        }}>
            <div className="glass-card" style={{
                borderRadius: "16px",
                padding: "24px",
                width: "100%",
                maxWidth: "480px",
            }}>
                <h2 style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "white",
                    marginBottom: "24px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                }}>
                    <svg style={{ width: "24px", height: "24px", color: BRAND_PRIMARY }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create New Bot
                </h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "24px" }}>
                    <div>
                        <label htmlFor="create-bot-instanceId" style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#9ca3af", marginBottom: "8px" }}>
                            Instance ID
                        </label>
                        <input
                            id="create-bot-instanceId"
                            placeholder="unique-bot-name"
                            value={newBot.instanceId}
                            disabled={creatingBot}
                            onChange={(e) => setNewBot({ ...newBot, instanceId: e.target.value })}
                            className="input-dark"
                            style={{ width: "100%" }}
                        />
                    </div>
                    <div>
                        <label htmlFor="create-bot-poolId" style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#9ca3af", marginBottom: "8px" }}>
                            Pool ID
                        </label>
                        <input
                            id="create-bot-poolId"
                            placeholder="Enter pool address"
                            value={newBot.poolId}
                            disabled={creatingBot}
                            onChange={(e) => setNewBot({ ...newBot, poolId: e.target.value })}
                            className="input-dark"
                            style={{ width: "100%" }}
                        />
                    </div>
                    <div>
                        <label htmlFor="create-bot-account" style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#9ca3af", marginBottom: "8px" }}>
                            Account
                        </label>
                        {accounts.length === 0 ? (
                            <div style={{ color: "#9ca3af", fontSize: "14px" }}>
                                No accounts yet.{" "}
                                <span
                                    onClick={() => navigate("/accounts")}
                                    style={{ color: "#c4b5fd", cursor: "pointer", textDecoration: "underline" }}
                                >
                                    Create one
                                </span>
                            </div>
                        ) : (
                            <div ref={accountDropdownRef} style={{ position: "relative" }}>
                                <button
                                    id="create-bot-account"
                                    type="button"
                                    disabled={creatingBot}
                                    onClick={() => setAccountDropdownOpen(!accountDropdownOpen)}
                                    className="input-dark"
                                    style={{
                                        width: "100%",
                                        fontSize: "14px",
                                        padding: "10px 12px",
                                        cursor: creatingBot ? "not-allowed" : "pointer",
                                        opacity: creatingBot ? 0.7 : 1,
                                        textAlign: "left",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {newBot.accountId
                                            ? (() => {
                                                const acc = accounts.find(a => a._id === newBot.accountId);
                                                return acc ? `${acc.name} (${acc.addresses[0]?.address?.slice(0, 6)}...${acc.addresses[0]?.address?.slice(-4)})` : "Select an account";
                                            })()
                                            : "Select an account"}
                                    </span>
                                    <svg
                                        style={{
                                            width: "16px",
                                            height: "16px",
                                            flexShrink: 0,
                                            marginLeft: "8px",
                                            transform: accountDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                                            transition: "transform 0.15s",
                                        }}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {accountDropdownOpen && !creatingBot && (
                                    <div style={{
                                        position: "absolute",
                                        top: "calc(100% + 4px)",
                                        left: 0,
                                        right: 0,
                                        background: "#1a1a2e",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        borderRadius: "8px",
                                        zIndex: 50,
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                                        maxHeight: "240px",
                                        overflowY: "auto",
                                    }}>
                                        {accounts.map((acc) => (
                                            <button
                                                key={acc._id}
                                                type="button"
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setNewBot({ ...newBot, accountId: acc._id });
                                                    setAccountDropdownOpen(false);
                                                }}
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "10px 12px",
                                                    background: acc._id === newBot.accountId ? "rgba(153,69,255,0.12)" : "transparent",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    textAlign: "left",
                                                    color: "white",
                                                    fontSize: "14px",
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (acc._id !== newBot.accountId) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (acc._id !== newBot.accountId) e.currentTarget.style.background = "transparent";
                                                }}
                                            >
                                                {acc.name} ({acc.addresses[0]?.address?.slice(0, 6)}...{acc.addresses[0]?.address?.slice(-4)})
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {creatingBot && (
                    <div
                        style={{
                            marginBottom: "12px",
                            padding: "10px 12px",
                            borderRadius: "10px",
                            border: "1px solid rgba(20,241,149,0.3)",
                            background: "rgba(20,241,149,0.08)",
                            color: "#d1fae5",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "13px",
                        }}
                    >
                        <svg style={{ width: "16px", height: "16px", animation: "spin 1s linear infinite" }} viewBox="0 0 24 24">
                            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Preparing bot instance... this can take a few seconds on Raydium/Meteora pools.
                    </div>
                )}
                {createBotError && (
                    <div
                        style={{
                            marginBottom: "12px",
                            padding: "10px 12px",
                            borderRadius: "10px",
                            border: "1px solid rgba(239,68,68,0.35)",
                            background: "rgba(239,68,68,0.08)",
                            color: "#fecaca",
                            fontSize: "13px",
                        }}
                    >
                        {createBotError}
                    </div>
                )}
                <div style={{ display: "flex", gap: "12px" }}>
                    <button
                        onClick={onCreateBot}
                        disabled={creatingBot}
                        className="btn-primary"
                        style={{ flex: 1, opacity: creatingBot ? 0.8 : 1, cursor: creatingBot ? "not-allowed" : "pointer" }}
                    >
                        {creatingBot ? "Creating..." : "Launch Bot"}
                    </button>
                    <button
                        onClick={onClose}
                        disabled={creatingBot}
                        style={{
                            flex: 1,
                            padding: "12px 24px",
                            borderRadius: "12px",
                            border: "1px solid #4b5563",
                            background: "transparent",
                            color: "#d1d5db",
                            cursor: creatingBot ? "not-allowed" : "pointer",
                            opacity: creatingBot ? 0.7 : 1,
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateBotModal;
